import { Flex, theme } from 'antd';
import { type Dayjs } from 'dayjs';
import { type ReactNode, useContext, useEffect, useState } from 'react';
import { type InvoiceFilters, type InvoicesFiltersProps } from './types';
import AuthorizedComponent from '../../../components/authorized-component';
import DateRangePicker from '../../../components/controls/date-range-picker';
import FilterSelect from '../../../components/controls/filter-select';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { type Customer } from '../../../entities';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
import { LocalDate } from '../../../utils/local-date';

const InvoicesFilter = (props: InvoicesFiltersProps): ReactNode => {
  const { session } = useContext(AuthContext);
  const { initialFilters, onChange } = props;
  const [filters, setFilter] = useState<InvoiceFilters>(initialFilters ?? {});
  const { token } = theme.useToken();
  const customers = useQuery<Customer[]>(
    session?.claims.role === Roles.ROLE_HHC_ADMIN && [
      'customer',
      {
        filter: 'isInactive==false',
        page: { limit: 10000 },
        sort: 'name',
        fields: { customer: ['name', 'customerSetting'] },
      },
    ]
  );

  useEffect(() => {
    onChange?.(filters);
  }, [filters]);

  return (
    <Flex gap={token.paddingXS} flex={1} wrap="wrap">
      <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
        <FilterSelect
          title="customers"
          value={filters?.customers}
          canSelectAll
          mode="multiple"
          options={customers.data}
          optionValueProp="id"
          optionLabelProp="name"
          onChange={(value) => {
            setFilter((prev) => ({
              ...prev,
              customers: value,
            }));
          }}
        />
      </AuthorizedComponent>
      <DateRangePicker
        onChange={(value) => {
          setFilter((prev) => ({
            ...prev,
            billMonth: value
              ? [new LocalDate(value[0]).startOf('M'), new LocalDate(value[1]).endOf('M')]
              : undefined,
          }));
        }}
        title="Bill Month"
        showOnlyDateRange={true}
        value={filters?.billMonth?.map((v) => v.toDayjs()) as [Dayjs, Dayjs] | undefined}
        picker="month"
        customDisplayFormat="MMM YYYY"
        customRangePreset={[
          {
            label: 'This month',
            value: [new LocalDate().startOf('M').toDayjs(), new LocalDate().endOf('M').toDayjs()],
            key: 'm',
          },
          {
            label: 'Last month',
            value: [
              new LocalDate().subtract(1, 'M').startOf('M').toDayjs(),
              new LocalDate().subtract(1, 'M').endOf('M').toDayjs(),
            ],
            key: '-m',
          },
          {
            label: 'This year',
            value: [new LocalDate().startOf('y').toDayjs(), new LocalDate().endOf('y').toDayjs()],
            key: 'y',
          },
          {
            label: 'Last year',
            value: [
              new LocalDate().subtract(1, 'y').startOf('y').toDayjs(),
              new LocalDate().subtract(1, 'y').endOf('y').toDayjs(),
            ],
            key: '-y',
          },
        ]}
      />
      <FilterSelect
        value={filters?.statuses}
        canSelectAll
        mode="multiple"
        options={[
          { id: 'PAID', name: 'Paid' },
          { id: 'UNPAID', name: 'Unpaid' },
        ]}
        optionValueProp="id"
        optionLabelProp="name"
        title="Status"
        onChange={(value) => {
          setFilter((prev) => ({
            ...prev,
            statuses: value,
          }));
        }}
      />
    </Flex>
  );
};

InvoicesFilter.defaultProps = {
  onChange: (_filters: InvoiceFilters) => {},
  initialFilters: {},
};

export default InvoicesFilter;
